import React from "react";
import Select from "react-select";

// Black Dashboard theme colors
const COLORS = {
  bg: "#27293d",
  bgDark: "#1e1e2f",
  border: "#2b3553",
  text: "rgba(255, 255, 255, 0.8)",
  textMuted: "rgba(255, 255, 255, 0.4)",
  primary: "#e14eca",
  hover: "rgba(255, 255, 255, 0.07)",
  selected: "rgba(225, 78, 202, 0.25)",
};

const darkStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "transparent",
    borderColor: state.isFocused ? COLORS.primary : COLORS.border,
    borderRadius: "0.4285rem",
    minHeight: "40px",
    height: "40px",
    boxShadow: "none",
    cursor: "pointer",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      borderColor: state.isFocused ? COLORS.primary : "rgba(255,255,255,0.25)",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 12px",
    height: "38px",
    display: "flex",
    alignItems: "center",
  }),
  singleValue: (base) => ({
    ...base,
    color: COLORS.text,
    fontSize: "0.875rem",
    margin: 0,
  }),
  placeholder: (base) => ({
    ...base,
    color: COLORS.textMuted,
    fontSize: "0.875rem",
    margin: 0,
  }),
  input: (base) => ({
    ...base,
    color: COLORS.text,
    fontSize: "0.875rem",
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? COLORS.primary : COLORS.textMuted,
    padding: "0 8px",
    transition: "color 0.2s ease, transform 0.2s ease",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
    "&:hover": { color: COLORS.text },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: COLORS.textMuted,
    padding: "0 4px",
    "&:hover": { color: COLORS.text },
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: COLORS.bg,
    border: `1px solid ${COLORS.border}`,
    borderRadius: "0.4285rem",
    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    zIndex: 9999,
    marginTop: "4px",
    overflow: "hidden",
  }),
  menuList: (base) => ({
    ...base,
    padding: "4px 0",
    maxHeight: "220px",
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(255,255,255,0.15)",
      borderRadius: "10px",
      "&:hover": { background: "rgba(255,255,255,0.25)" },
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? COLORS.selected
      : state.isFocused
      ? COLORS.hover
      : "transparent",
    color: state.isSelected ? COLORS.primary : COLORS.text,
    fontSize: "0.875rem",
    padding: "10px 15px",
    cursor: "pointer",
    transition: "background-color 0.2s ease, color 0.2s ease",
    "&:active": { backgroundColor: COLORS.selected },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: COLORS.textMuted,
    fontSize: "0.875rem",
  }),
};

/**
 * FSelect — themed react-select drop-in for Black Dashboard dark theme.
 *
 * Compatible with Reactstrap FormGroup + Label pattern.
 * onChange fires a synthetic event: { target: { name, value } }
 * - Single: value es el valor escalar seleccionado
 * - Multi (isMulti=true): value es un array de valores escalares
 */
function FSelect({
  name,
  value,
  onChange,
  options = [],
  placeholder = "Seleccionar...",
  isDisabled = false,
  isClearable = false,
  isMulti = false,
  id,
}) {
  const multiStyles = isMulti
    ? {
        ...darkStyles,
        valueContainer: (base) => ({
          ...base,
          padding: "4px 12px",
          minHeight: "38px",
          height: "auto",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
        }),
        control: (base, state) => ({
          ...darkStyles.control(base, state),
          height: "auto",
          minHeight: "40px",
        }),
        multiValue: (base) => ({
          ...base,
          backgroundColor: "rgba(225,78,202,0.2)",
          borderRadius: "4px",
        }),
        multiValueLabel: (base) => ({
          ...base,
          color: "rgba(255,255,255,0.85)",
          fontSize: "0.8rem",
        }),
        multiValueRemove: (base) => ({
          ...base,
          color: "rgba(255,255,255,0.5)",
          "&:hover": { backgroundColor: "rgba(225,78,202,0.4)", color: "#fff" },
        }),
      }
    : darkStyles;

  const selected = isMulti
    ? options.filter((o) => Array.isArray(value) && value.map(String).includes(String(o.value)))
    : options.find((o) => String(o.value) === String(value)) || null;

  const handleChange = (option) => {
    if (isMulti) {
      onChange({ target: { name, value: option ? option.map((o) => o.value) : [] } });
    } else {
      onChange({ target: { name, value: option ? option.value : "" } });
    }
  };

  return (
    <div className="react-select-container-custom" style={{ display: "block", width: "100%" }}>
      <Select
        inputId={id}
        name={name}
        value={selected}
        onChange={handleChange}
        options={options}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isClearable={isClearable}
        isMulti={isMulti}
        styles={multiStyles}
        noOptionsMessage={() => "Sin opciones"}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        classNamePrefix="fselect"
      />
    </div>
  );
}

export default FSelect;
